import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { FOUNDER, STONE_LINEN, CONTACT } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];

const values = [
  { title: "Organised", text: "Every detail tracked, every decision considered, nothing left to chance." },
  { title: "Design-led", text: "A genuine eye for warm, natural, beautifully resolved spaces." },
  { title: "Reliable", text: "A trusted point of contact who keeps the project calm and on course." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-cream">
      {/* Hero */}
      <section className="pt-36 sm:pt-44 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-3xl"
          >
            <Overline>About · Paige Schonell</Overline>
            <h1 className="font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6">
              It started with my own home.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story split */}
      <section className="pb-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 items-start">
          <Reveal className="lg:col-span-6 lg:sticky lg:top-28">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={FOUNDER} alt="Paige Schonell" className="absolute inset-0 h-full w-full object-cover object-top" />
            </div>
          </Reveal>

          <div className="lg:col-span-6 space-y-7">
            <Reveal>
              <p className="text-muted-foreground text-lg font-light leading-relaxed">
                After renovating my own home, I discovered a passion not only for creating beautiful
                spaces, but for managing the many moving parts that bring a renovation to life.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-muted-foreground font-light leading-relaxed">
                Today, I work closely with homeowners across Sydney's Lower North Shore, overseeing
                everything from interior selections and material sourcing through to trade coordination
                and project management. Whether it's a small cosmetic refresh or a full-scale
                renovation, my goal is to take the stress out of the journey.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-muted-foreground font-light leading-relaxed">
                From concept to completion, my goal is to take the stress out of renovating — so
                clients can enjoy the process without the overwhelm of managing trades, timelines and
                endless decisions themselves.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-muted-foreground font-light leading-relaxed">
                I coordinate a trusted team of trades and suppliers, manage the details, and ensure
                every decision contributes to a beautifully executed result. My approach is calm,
                organised and personal — because a renovation should feel like a guided experience,
                not an overwhelming one.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="font-serif text-ink text-2xl sm:text-3xl leading-snug pt-4">
                "I'm here to hold the details together, so you can enjoy seeing your home come to life."
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Overline>My approach</Overline></Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-border">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="bg-cream h-full p-8 lg:p-10">
                  <span className="font-serif text-4xl text-taupe">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-ink mt-5">{v.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-28 sm:py-36 overflow-hidden">
        <img src={STONE_LINEN} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="relative max-w-3xl mx-auto px-6 text-center text-cream">
          <Reveal>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.1]">
              Let's talk about your home.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="about-cta-link"
              className="inline-flex items-center gap-2 mt-9 bg-cream text-ink px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-cream/90 transition-colors"
            >
              Start a conversation <ArrowRight size={15} />
            </Link>
          </Reveal>
          <p className="mt-6 text-cream/70 text-sm font-light">{CONTACT.location}</p>
        </div>
      </section>
    </div>
  );
}
